import {
  CheckCircleFilled,
  MinusCircleFilled,
  PlusCircleFilled,
  SearchOutlined,
  StopFilled,
  UserOutlined,
} from "@ant-design/icons";
import { Button, Input } from "antd";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import {
  useAddUserToGroup2,
  useLeaveGroup2,
  useRemoveUserToGroup2,
} from "../../api/api";
import { useDeleteGroup2 } from "../../api/api.delete";
import {
  useGetGroupById2,
  useGetGroupUsers2,
  useSearchUserByEmail,
} from "../../api/api.get";
import ConfirmDialog from "../../components/dialog/confirm.dialog";
import { useNotifications } from "../../components/notifications/notification";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { useAuthContext } from "../../providers/auth.provider";
import { IExhutAuthUser, IGroupUser } from "../../types/types";
import { capitalize } from "../../utils/functions";

export default function GroupPage() {
  const { gid } = useParams();
  const { user } = useAuthContext();
  const { data } = useGetGroupById2(gid);

  const { data: groupUsersData, refetch: refetchGroupUsers } =
    useGetGroupUsers2(gid);

  const { notification, notifyError, notifySuccess } = useNotifications();
  const { mutateAsync: deleteAsync } = useDeleteGroup2();

  const { mutateAsync: addUserToGroupAsync } = useAddUserToGroup2();
  const { mutateAsync: removeUserToGroupAsync } = useRemoveUserToGroup2();

  const { mutateAsync: leaveGroupAsync } = useLeaveGroup2();

  const [confirmDeleteGroupId, setConfirmDeleteGroupId] = useState<string>();
  const [showConfirmLeaveGroup, setShowConfirmLeaveGroup] = useState<boolean>();

  const [confirmRemoveUser, setConfirmRemoveUser] = useState<string>();

  const { mutateAsync: searchedUserDataAsync } = useSearchUserByEmail();

  const [searchResults, setSearchResults] = useState<
    IExhutAuthUser[] | undefined
  >(undefined);

  const groupUserUsernames = groupUsersData?.map((u) => u.username);

  const handleSearch = () => {
    searchedUserDataAsync({
      email: formik.values.email,
    })
      .then((results) => {
        const users = results.filter(
          (u) => !groupUserUsernames?.includes(u.username),
        );
        setSearchResults(users);
      })
      .catch(() => notifyError("Whoops! Unable to find data"));
  };

  const handleDeleteGroup = () => {
    setConfirmDeleteGroupId(undefined);
    deleteAsync({ groupId: confirmDeleteGroupId! })
      .then(() => {
        navigate("/groups");
      })
      .catch(() => notifyError("Please try again"));
  };

  const handleAddUserToGroup = () => {
    if (!searchResults || (searchResults && searchResults.length === 0)) {
      notifyError("Please select user");
      return;
    }

    addUserToGroupAsync({
      groupId: data?.id!,
      memberUsername: searchResults?.[0].username,
    })
      .then(() => {
        notifySuccess({
          title: "Success",
          description: "User successfully added",
        });
        formik.resetForm();
        setSearchResults([]);
        refetchGroupUsers();
      })
      .catch(() => notifyError("Whoops! Please try again"));
  };

  const handleRemoveUserFromGroup = () => {
    removeUserToGroupAsync({
      groupId: data?.id!,
      member: confirmRemoveUser!,
    })
      .then(() => {
        notifySuccess({
          title: "Success",
          description: "User successfully removed",
        });
        setConfirmRemoveUser(undefined);
        refetchGroupUsers();
      })
      .catch(() => notifyError("Whoops! Please try again"));
  };

  const handleLeaveGroup = () => {
    leaveGroupAsync({
      groupId: data?.id!,
    })
      .then(() => {
        notifySuccess({
          title: "Success",
          description: "User successfully removed",
        });
        navigate(`/groups`);
        refetchGroupUsers();
      })
      .catch(() => notifyError("Whoops! Please try again"));
  };

  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    initialTouched: {
      email: true,
    },
    validationSchema: Yup.object({
      email: Yup.string().required("Please enter full email address"),
    }),
    onSubmit: async (values) => {
      handleSearch();
    },
  });

  const isOwner = useMemo(() => {
    return data?.createdBy === user?.username;
  }, [data, user]);

  return (
    <div className="min-h-screen max-w-5xl mx-auto">
      {notification}

      <ConfirmDialog
        open={!!confirmDeleteGroupId}
        title="Are you sure you would like to delete this group?"
        subtitle="All data attached to this group will be removed."
        onOk={handleDeleteGroup}
        onCancel={() => setConfirmDeleteGroupId(undefined)}
      />

      <ConfirmDialog
        open={!!showConfirmLeaveGroup}
        title="Are you sure you would like to leave this group?"
        subtitle="All data of yours in this group will be removed and cannot be reclaimed."
        onOk={handleLeaveGroup}
        onCancel={() => setShowConfirmLeaveGroup(false)}
      />
      <ConfirmDialog
        open={!!confirmRemoveUser}
        title="Are you sure you want to remove this user from this group?"
        subtitle="They will no longer be able to see any of the data related to this group."
        onOk={handleRemoveUserFromGroup}
        onCancel={() => setConfirmRemoveUser(undefined)}
      />
      <main className="pt-8 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-row justify-between">
            <div className="mb-8 flex flex-col items-start gap-3">
              <button
                className="text-blue-500 text-sm"
                onClick={() => navigate("/groups")}
              >
                Back
              </button>

              <div className="flex flex-col">
                <span className="text-sm text-gray-800 mb-1">Group Name</span>
                <span className="text-3xl font-bold text-foreground mb-2">
                  {capitalize(data?.name)}
                </span>
                <p className="text-muted-foreground text-gray-500 text-sm">
                  Created On: {dayjs(data?.createdAt).format("DD MMM YYYY")}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 justify-end mb-8">
              {isOwner && (
                <Button
                  type="primary"
                  danger
                  onClick={() => setConfirmDeleteGroupId(data?.id)}
                >
                  Delete Group
                </Button>
              )}

              {!isOwner && (
                <Button
                  type="default"
                  danger
                  onClick={() => setShowConfirmLeaveGroup(true)}
                >
                  Leave Group
                </Button>
              )}
            </div>
          </div>

          <div
            className="text-sm px-4 py-4 rounded-md bg-gray-100 font-semibold border-gray-600 flex flex-col
                mt-3 mb-6 gap-2"
          >
            <p
              className="text-xs font-semibold
                text-gray-600"
            >
              * You can add any number of users to a group or remove them at
              your own convenience.
            </p>

            <p
              className="text-xs font-semibold
                text-gray-600"
            >
              * There is no invitation send to members. They will be added
              automatically to this group.
            </p>
            <p
              className="text-xs font-semibold
                text-gray-600"
            >
              * Group members can also leave the group at any time.
            </p>

            <p
              className="text-xs font-semibold
                text-gray-600"
            >
              * Group members data (their expenses, savings etc.) will not be
              removed even after they are removed from the group.
            </p>
          </div>

          {/* Two Panel Layout */}
          <div
            className={`grid grid-cols-1 ${isOwner ? "lg:grid-cols-3" : "lg:grid-cols-2"} gap-6`}
          >
            {/* Left Panel: Accounts List */}

            <div className="lg:col-span-2">
              {groupUsersData && groupUsersData.length > 0 && (
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      Members
                    </CardTitle>
                    <CardDescription>
                      {(groupUsersData ?? []).length} Member
                      {groupUsersData?.length !== 1 ? "s" : ""}{" "}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {groupUsersData.map((member: IGroupUser) => {
                        return (
                          <div
                            key={member.username}
                            className="p-4 border border-slate-200 rounded-lg hover:shadow-md hover:border-primary/50 transition cursor-pointer bg-slate-50 hover:bg-slate-100"
                          >
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex flex-row items-center justify-center gap-4">
                                <UserOutlined />
                                <h3 className="font-semibold text-foreground text-sm">
                                  {!!member.firstName
                                    ? capitalize(member.lastName) +
                                      ", " +
                                      capitalize(member.firstName)
                                    : member.email}
                                </h3>
                                {member.username === user?.username && (
                                  <span className="text-xs border bg-blue-500 px-2 rounded-full text-white py-1">
                                    Me
                                  </span>
                                )}
                              </div>
                              <div className="flex gap-2 items-center">
                                {member.username === data?.createdBy ? (
                                  <div className="bg-green-300 text-xs justify-center align-center text-center rounded-full px-4 py-2">
                                    Owner
                                  </div>
                                ) : (
                                  <span className="text-sm px-2">Member</span>
                                )}

                                <div className="w-5">
                                  {isOwner &&
                                    member.username !== user?.username && (
                                      <Button
                                        icon={
                                          <MinusCircleFilled className="text-red-500" />
                                        }
                                        type="text"
                                        onClick={(e) => {
                                          setConfirmRemoveUser(member.username);
                                        }}
                                      />
                                    )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Panel: Add Account Form */}
            <div className="flex flex-col gap-8">
              {isOwner && (
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="text-xl">Add User</CardTitle>
                    <CardDescription className="text-gray-500">
                      Please enter full email address of the user.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={formik.handleSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-foreground mb-2">
                          User Email
                        </label>
                        <Input
                          type="text"
                          placeholder="e.g., jane.doe@yahoo.com"
                          {...formik.getFieldProps("email")}
                          required
                        />
                        {!!formik.errors.email && (
                          <p className="text-red-500 text-xs">
                            {formik.errors.email}
                          </p>
                        )}
                      </div>

                      {searchResults && searchResults.length > 0 && (
                        <div className="flex flex-col gap-3 border rounded-lg border-green-500 bg-green-100/30 px-4 py-2">
                          <div className="flex flex-row gap-1">
                            <CheckCircleFilled style={{ color: "limegreen" }} />
                            <span className="text-xs">User Found</span>
                          </div>
                          <div className="flex flex-row gap-1">
                            <input type="radio" checked={true} />
                            <span className="text-gray-600 text-xs">
                              {searchResults?.[0].firstName
                                ? searchResults?.[0].lastName +
                                  ", " +
                                  searchResults?.[0].firstName
                                : searchResults?.[0].email}
                            </span>
                          </div>
                        </div>
                      )}
                      {searchResults &&
                        searchResults.length === 0 &&
                        formik.values.email.length > 0 && (
                          <div className="flex flex-col gap-3 border rounded-lg border-red-500 bg-red-100/30 px-4 py-2">
                            <div className="flex flex-row gap-3">
                              <StopFilled style={{ color: "red" }} />
                              <span className="text-xs">No users found.</span>
                            </div>
                          </div>
                        )}

                      {/* Buttons */}
                      <div className="flex justify-between gap-2 pt-4">
                        {searchResults && searchResults.length > 0 ? (
                          <Button
                            className="flex-1"
                            onClick={() => handleAddUserToGroup()}
                            type="primary"
                            icon={<PlusCircleFilled />}
                            disabled={searchResults.length === 0}
                          >
                            Add User
                          </Button>
                        ) : (
                          <Button
                            className="flex-1"
                            onClick={formik.submitForm}
                            type="primary"
                            icon={<SearchOutlined />}
                            disabled={
                              !!formik.errors.email ||
                              formik.values.email.trim().length === 0
                            }
                          >
                            Search
                          </Button>
                        )}

                        <Button
                          className="flex-1"
                          onClick={() => {
                            formik.resetForm();
                          }}
                          type="text"
                        >
                          Clear
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
